import { Pagination } from 'antd';
import React, { useContext, useEffect } from 'react';
import { OperationType } from 'relay-runtime';
import { v4 as uuidv4 } from 'uuid';

import { PAGE_SIZE_ARRAY } from '../const';
import useDispatchSetterStore from '../hooks/useDispatchSetterStore';
import useDynamicSelector from '../hooks/useDynamicSelector';
import { SmxPropsTableContext } from '../providers/propsTableProvider';
import { useRouting } from '../routing/routingProvider';
import { getSetterTable, ITableState } from '../store/slices/tableSlice';
import PaginationPanelContainer from './PaginationPanelContainer';

export interface ITableDbPagination<_TQuery extends OperationType> {
    count: number;
    sizeOfPage?: string[];
}

const TableDbPagination = <TQuery extends OperationType>({ count, sizeOfPage }: ITableDbPagination<TQuery>) => {
    const { nameQuery, prefixStore } = useContext(SmxPropsTableContext);
    const setterTable = useDispatchSetterStore<ITableState>(getSetterTable(nameQuery + prefixStore));

    const page = useDynamicSelector('table', 'page');
    const pageSize = useDynamicSelector('table', 'pageSize');
    const filterFlexible = useDynamicSelector('table', 'flexibleFilters');
    const orderBy = useDynamicSelector('table', 'orderBy');

    const { setUrlParam } = useRouting();

    const oldPageSize = pageSize;

    const handlePaginationChange = (page: number, pageSize: number) => {
        const newStatePaginationStore: any = { page, pageSize, filterFlexible, orderBy };
        if (oldPageSize !== pageSize) {
            newStatePaginationStore.prefixFetchKey = uuidv4();
        }
        setterTable(newStatePaginationStore);
    };

    useEffect(() => {
        setUrlParam({ page, pageSize });
    }, [page, pageSize]);

    if (count && pageSize * (page - 1) > count) {
        setTimeout(() => {
            handlePaginationChange(1, pageSize);
        });
    }

    return (
        <PaginationPanelContainer>
            <Pagination
                key={uuidv4()}
                onChange={handlePaginationChange}
                defaultCurrent={page}
                total={count}
                pageSize={pageSize}
                pageSizeOptions={sizeOfPage || PAGE_SIZE_ARRAY}
                showSizeChanger
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
            />
        </PaginationPanelContainer>
    );
};

export default TableDbPagination;
